import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AuthUser,
  fetchCurrentUser,
  login as loginRequest,
  readStoredToken,
  setUnauthorizedHandler,
  storeToken,
} from './api';

type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Convenience for the many places that only care about the admin bit. */
  isAdmin: boolean;
  /** Sees and edits every strategy, whoever owns it. */
  isSuperAdmin: boolean;
  token: string | null;
  /** Message to surface on the login screen, e.g. after a session expires. */
  notice: string | null;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: (notice?: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const signOut = useCallback((message?: string) => {
    storeToken(null);
    setToken(null);
    setUser(null);
    setNotice(message ?? null);
    setStatus('anonymous');
  }, []);

  // Any 401 from the backend drops us straight back to the login screen.
  useEffect(() => {
    setUnauthorizedHandler(() => signOut('Your session has expired'));
    return () => setUnauthorizedHandler(null);
  }, [signOut]);

  // On boot, confirm a stored token is still valid before showing the dashboard.
  useEffect(() => {
    const stored = readStoredToken();
    if (!stored) {
      setStatus('anonymous');
      return;
    }

    let cancelled = false;
    storeToken(stored);

    fetchCurrentUser()
      .then((me) => {
        if (cancelled) return;
        setToken(stored);
        setUser(me);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        storeToken(null);
        setStatus('anonymous');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await loginRequest(username, password);
    storeToken(result.token);
    setToken(result.token);
    setUser(result.user);
    setNotice(null);
    setStatus('authenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAdmin: user?.role === 'admin',
      isSuperAdmin: user?.isSuperAdmin === true,
      token,
      notice,
      signIn,
      signOut,
    }),
    [status, user, token, notice, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}
